import { useEffect } from 'react';
import { Stack, Typography, Popover, useTheme, alpha } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { Delete } from '@material-ui/icons';
import dayjs from 'dayjs';

import { FormProvider, RHFCheckbox, RHFTimePicker, Scrollbar } from '@app/components';
import { Strings } from '@app/constants';
import { useRequestState } from '@app/hooks';
import { addDate, deleteDate, updateDate } from '../slice';
import {
  selectAddDateState,
  selectCardDetailState,
  selectDeleteDateState,
  selectUpdateDateState,
} from '../selector';

interface Props {
  openTimeLimitPopup: HTMLElement | null;
  handleCloseDatePicker: () => void;
  cardId: string | undefined;
  onClose?: () => void;
  startsDate?: string;
  endDate?: string;
  endTime?: string;
  update?: boolean;
  startTime?: string;
}
const AddTimeLimitPopup = ({
  openTimeLimitPopup,
  handleCloseDatePicker,
  cardId,
  onClose,
  startsDate,
  startTime,
  endDate,
  endTime,
  update,
}: Props) => {
  const open = Boolean(openTimeLimitPopup);
  const id = open ? 'simple-popover' : undefined;
  const methods = useForm<any>({
    defaultValues: {
      startTime: startTime ? dayjs(startTime) : null,
      startDate: startsDate ? dayjs(startsDate) : null,
      endDate: endDate ? dayjs(endDate) : null,
      endTime: endTime ? dayjs(endTime) : null,
      startDateCheckbox: !!startsDate,
    },
  });
  useEffect(() => {
    methods.reset({
      startTime: startTime ? dayjs(startTime) : null,
      startDate: startsDate ? dayjs(startsDate) : null,
      endDate: endDate ? dayjs(endDate) : null,
      endTime: endTime ? dayjs(endTime) : null,
      startDateCheckbox: !!startsDate,
    });
  }, [endDate, endTime, methods, startTime, startsDate]);

  const { control } = useForm();

  const dispatch = useDispatch();
  const theme = useTheme();

  useWatch({ control: methods.control, name: 'startDateCheckbox' });
  useWatch({ control: methods.control, name: 'dueDateCheckBox' });

  const data = useSelector(selectCardDetailState);

  const onSubmit = (form: any) => {
    update
      ? dispatch(updateDate({ ...form, dateId: data?.data?.dueDates?.id }))
      : dispatch(
          addDate({
            ...form,
            isCompleted: false,
            cardId: Number(cardId),
          })
        );
  };

  const { loading } = useRequestState({
    stateSelector: update ? selectUpdateDateState : selectAddDateState,
    onSuccess: onClose,
    errorShown: true,
  });

  const { loading: deleteLoading } = useRequestState({
    stateSelector: selectDeleteDateState,
  });

  const startDate = useWatch({ control, name: 'startDate' });
  return (
    <>
      <FormProvider methods={methods}>
        <Stack sx={{ width: '30%' }} mt={4} ml={0}>
          <Popover
            id={id}
            open={open}
            anchorEl={openTimeLimitPopup}
            onClose={handleCloseDatePicker}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            sx={{
              '& .MuiPaper-root': {
                width: '370px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: alpha(theme.palette.error.contrastText, 0.1),
                backdropFilter: 'blur(10px)',
                borderRadius: '10px',
                boxShadow: theme.shadows[5],
              },
            }}
          >
            <Scrollbar>
              <Stack sx={{ display: 'flex', textAlign: 'center' }} pt={1}>
                <Typography variant="subtitle2">{Strings.taskBoard.dates}</Typography>
              </Stack>
              <Stack ml={0.5}>
                <Stack px={1.5}>
                  <Typography>{Strings.taskBoard.startDate}</Typography>
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      width: '100%',
                    }}
                    mt={1}
                  >
                    <RHFCheckbox
                      sx={{
                        '&.Mui-checked': {
                          color: theme.palette.taskboard.raisinBlack,
                        },
                      }}
                      checked={methods.getValues('startDateCheckbox')}
                      name={'startDateCheckbox'}
                      label={undefined}
                    />
                    <Stack gap={0.5} sx={{ flexDirection: 'row', width: '100%' }}>
                      <Stack sx={{ width: '54%' }}>
                        <Controller
                          name="startDate"
                          control={methods.control}
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              minDate={dayjs()}
                              onChange={(date) => {
                                field.onChange(date);
                              }}
                              sx={{ '& .MuiInputBase-input': { height: 25, padding: 1 } }}
                              disabled={!methods.getValues('startDateCheckbox')}
                            />
                          )}
                        />
                      </Stack>
                      <RHFTimePicker
                        name="startTime"
                        size="small"
                        label={Strings.taskBoard.time}
                        sx={{ width: '56%' }}
                        disabled={!methods.getValues('startDateCheckbox')}
                      />
                    </Stack>
                  </Stack>
                </Stack>
                <Stack mt={1} px={1.5}>
                  <Typography>{Strings.taskBoard.dueDate}</Typography>
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      width: '100%',
                    }}
                    mt={1}
                  >
                    <RHFCheckbox
                      checked={!methods.getValues('dueDateCheckbox')}
                      sx={{
                        '&.Mui-checked': {
                          color: theme.palette.taskboard.raisinBlack,
                        },
                      }}
                      name="dueDateCheckbox"
                      label=""
                    />
                    <Stack gap={0.5} sx={{ flexDirection: 'row', width: '100%' }}>
                      <Stack sx={{ width: '54%' }}>
                        <Controller
                          name="endDate"
                          control={methods.control}
                          render={({ field }) => (
                            <DatePicker
                              minDate={startDate || dayjs()}
                              {...field}
                              onChange={(date) => {
                                field.onChange(date);
                              }}
                              sx={{ '& .MuiInputBase-input': { height: 25, padding: 1 } }}
                              disabled={methods.getValues('dueDateCheckBox')}
                            />
                          )}
                        />
                      </Stack>
                      <RHFTimePicker
                        name="endTime"
                        size="small"
                        label={Strings.taskBoard.time}
                        sx={{ width: '56%' }}
                        disabled={methods.getValues('dueDateCheckbox')}
                      />
                    </Stack>
                  </Stack>
                </Stack>
                <Stack m={1} mt={2} gap={1} flexDirection={'row'}>
                  <LoadingButton
                    loading={loading}
                    variant="contained"
                    onClick={methods.handleSubmit(onSubmit)}
                    sx={{
                      bgcolor: theme.palette.taskboard.raisinBlack,
                      '&:hover': {
                        bgcolor: theme.palette.taskboard.raisinBlack,
                      },
                      flexGrow: 1,
                    }}
                  >
                    {Strings.taskBoard.save}
                  </LoadingButton>
                  {data?.data?.dueDates !== null && (
                    <LoadingButton
                      variant="outlined"
                      loading={deleteLoading}
                      onClick={() => {
                        dispatch(deleteDate({ dateId: data?.data?.dueDates?.id }));
                        handleCloseDatePicker();
                        onClose?.();
                      }}
                      sx={{
                        color: theme.palette.taskboard.raisinBlack,
                        borderColor: theme.palette.taskboard.raisinBlack,
                        '&:hover': {
                          borderColor: theme.palette.taskboard.raisinBlack,
                        },
                        ml: 1,
                      }}
                    >
                      <Delete />
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
            </Scrollbar>
          </Popover>
        </Stack>
      </FormProvider>
    </>
  );
};
export { AddTimeLimitPopup };
